import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import { VERIFY_USER } from './graphql/queries';
import { AUTHENTICATE_USER, LOGOUT_USER } from './graphql/mutations';
import { jsx as _jsx } from "react/jsx-runtime";
const AuthContext = /*#__PURE__*/createContext(null);
const AuthProvider = _ref => {
  let {
    children,
    user,
    setUser,
    setIsUnauthDialogOpen
  } = _ref;
  const [verifyUser] = useLazyQuery(VERIFY_USER, {
    fetchPolicy: 'no-cache',
    onCompleted: verifyUserData => {
      if (verifyUserData && verifyUserData.verifyUser) {
        setUser(verifyUserData.verifyUser);
      } else {
        setUser(null);
      }
    }
  });
  useEffect(() => {
    verifyUser();
  }, [verifyUser]);
  const [authenticate, authenticateResult] = useMutation(AUTHENTICATE_USER, {
    onCompleted: data => {
      var _data$authenticateUse;
      if (data !== null && data !== void 0 && (_data$authenticateUse = data.authenticateUser) !== null && _data$authenticateUse !== void 0 && _data$authenticateUse.user) {
        setUser(data.authenticateUser.user);
      } else {
        setUser(null);
      }
    }
  });
  const [logout] = useMutation(LOGOUT_USER, {
    onCompleted: data => {
      var _data$logoutUser;
      if (((_data$logoutUser = data.logoutUser) === null || _data$logoutUser === void 0 ? void 0 : _data$logoutUser.response) === 'SUCCESS') {
        setUser(null);
      }
    }
  });
  const authenticateUser = useCallback(authenticateUserRequest => {
    const {
      allowSendingChallengeEmail,
      authenticationType,
      orderNumber,
      email,
      token,
      loginSource
    } = authenticateUserRequest;
    return authenticate({
      variables: {
        allowSendingChallengeEmail,
        authenticationType,
        orderNumber,
        email,
        token,
        loginSource
      }
    });
  }, [authenticate]);
  const logoutUser = useCallback(logout, [logout]);
  const authenticatedAction = useCallback(async action => {
    const {
      data: verifyUserData
    } = await verifyUser();
    const verifiedUser = verifyUserData === null || verifyUserData === void 0 ? void 0 : verifyUserData.verifyUser;
    if (verifiedUser) {
      action();
    } else {
      setIsUnauthDialogOpen(true);
    }
  }, [setIsUnauthDialogOpen, verifyUser]);
  const authenticateUserResult = useMemo(() => authenticateResult, [authenticateResult]);
  const value = useMemo(() => ({
    user,
    authenticateUser,
    authenticateUserResult,
    logoutUser,
    verifyUser,
    authenticatedAction
  }), [user, authenticateUser, authenticateUserResult, logoutUser, verifyUser, authenticatedAction]);
  return /*#__PURE__*/_jsx(AuthContext.Provider, {
    value: value,
    children: children
  });
};
const useAuth = () => {
  const context = useContext(AuthContext);
  if (isEmpty(context)) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return {
    ...context
  };
};
export { AuthProvider, useAuth };
//# sourceMappingURL=AuthContext.js.map